/**
 * SPEC-2 senior-prefs — pure helpers for the Senior list's saved filters and
 * all-days search. No React, no DOM: qa-list.tsx imports these so the rules
 * ("which saved filter is active", "may I save", "what does the strip say",
 * "how is the completed cell formatted in search mode") are testable on their
 * own and cannot drift between the page and the tests.
 */

import type { SavedFilter, SavedFilterSort } from "@shared/notification-preferences";
import { OPS_TZ } from "@/lib/ops-tz";

/** The list's default sort: the state that counts as "nothing selected" together with five empty sets. */
export const DEFAULT_LIST_SORT: SavedFilterSort = "completed_asc";

/** D3 — a search returns at most this many rows (newest first) plus the total. */
export const SENIOR_SEARCH_LIMIT = 50;

/** The five dropdowns a saved filter captures (D1). Never date, tab, q or attention. */
export const FILTER_SET_KEYS = ["agentStatus", "owner", "driver", "match", "bag"] as const;
export type FilterSetKey = (typeof FILTER_SET_KEYS)[number];

/** What the list page holds for the five dropdowns + sort. */
export interface SeniorFilterState {
  sort: SavedFilterSort;
  agentStatus: string[];
  owner: string[];
  driver: string[];
  match: string[];
  bag: string[];
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  return Array.from(sa).every((v) => sb.has(v));
}

/** True when the current state deep-equals the filter: same sort, same set contents ignoring order. */
export function isFilterActive(
  state: SeniorFilterState,
  filter: Pick<SavedFilter, "sort" | FilterSetKey>,
): boolean {
  if (state.sort !== filter.sort) return false;
  return FILTER_SET_KEYS.every((k) => sameSet(state[k], filter[k] ?? []));
}

/** "Nothing is selected": all five sets empty and the sort is the default. */
export function isDefaultFilterState(state: SeniorFilterState): boolean {
  return state.sort === DEFAULT_LIST_SORT && FILTER_SET_KEYS.every((k) => state[k].length === 0);
}

/** The saved filter the current state matches, or null. */
export function findActiveSavedFilter(state: SeniorFilterState, filters: readonly SavedFilter[]): SavedFilter | null {
  return filters.find((f) => isFilterActive(state, f)) ?? null;
}

/**
 * The PATCH body for a new filter: name + sort + the five sets, copied.
 * No id (the server assigns it) and never date / tab / q / attention.
 */
export function buildSavedFilter(name: string, state: SeniorFilterState): Omit<SavedFilter, "id"> {
  return {
    name: name.trim(),
    sort: state.sort,
    agentStatus: [...state.agentStatus],
    owner: [...state.owner],
    driver: [...state.driver],
    match: [...state.match],
    bag: [...state.bag],
  };
}

/** Search mode = the (debounced) search text is non-blank. */
export function isSearchMode(q: string | null | undefined): boolean {
  return typeof q === "string" && q.trim().length > 0;
}

/** The sentence shown above the table while a search is active (§4.2). */
export function searchStripText(q: string, total: number, limit: number = SENIOR_SEARCH_LIMIT): string {
  const noun = total === 1 ? "result" : "results";
  let text = `Searching all days for "${q.trim()}" · ${total} ${noun}, newest first · date, tab and filters are ignored while searching`;
  if (total > limit) text += ` · showing newest ${limit} of ${total}`;
  return text;
}

/**
 * Completed cell text in OPS_TZ.
 *   day    → "Sep 20 2:41 PM"          (the list's existing rendering)
 *   search → "Sun 20 Sep, 2:41 PM"     (rows span months, so the weekday helps)
 * Returns null for a missing or unparseable timestamp.
 */
export function formatCompletedAt(iso: string | null | undefined, mode: "day" | "search"): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString("en-US", { timeZone: OPS_TZ, hour: "numeric", minute: "2-digit", hour12: true });
  if (mode === "day") {
    const day = d.toLocaleDateString("en-CA", { timeZone: OPS_TZ, month: "short", day: "numeric" });
    return `${day} ${time}`;
  }
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: OPS_TZ, weekday: "short", day: "numeric", month: "short" }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("weekday")} ${get("day")} ${get("month")}, ${time}`;
}
